// Parimutuel pricing math — TypeScript mirror of the Supabase RPC.
//
// Per Session B revision (post-gate-4, 2026-05-13), the cockpit's
// pledge form needs a local approximation of slippage that matches
// the canonical RPC math rather than the linear linear-interpolation
// previously used in lib/priceImpact. The gate-4 surprise was that
// the form showed +22.54% slippage while the RPC computed NULL
// (discovery-territory NULL guard, now fixed in Phase A migration
// 20260513000001) — the two diverged on the discovery-territory
// edge case.
//
// This module mirrors the revised PL/pgSQL math from
// hub-ui/supabase/migrations/20260513000000_parimutuel_engine_port.sql
// (pre_pledge_price, post_pledge_price, compute_effective_price)
// and 20260513000001_slippage_discovery_territory_fix.sql
// (slippage_pct three-regime). The pledge form imports from here
// for its pre-flight preview; the canonical answer at pledge time
// still comes from the place_pledge RPC.
//
// Drift discipline:
//   * Single source of truth is the Supabase RPC.
//   * hub-ui/scripts/test_pricing_typescript.mjs runs the 13 sanity
//     cases against both this module and the Supabase RPC, asserts
//     identical output to within Decimal-rounding tolerance.
//   * hub-ui/scripts/test_supabase_pricing.py runs the same 13
//     cases against Python (engine/pricing.py) and the Supabase RPC.
//   * Three sources, two drift gates, one source of truth.
//
// Precision: JS native number is float64 (~15 significant digits).
// The engine uses mpmath at 30 digits; Postgres NUMERIC is
// arbitrary precision. For the cockpit's pre-flight preview
// purposes, float64 is sufficient — divergence between float64 and
// NUMERIC is below the rounding tolerance the drift test uses.

// ─── pre_pledge_price ────────────────────────────────────────────────
//
// The displayed outcome price immediately before a hypothetical
// pledge lands. Empty-pool branch: when T = 0 (no pledges at all),
// every outcome's displayed price is 1/n_outcomes (uniform prior).
// General case: p_i / T (share of pool on this outcome).
export function prePledgePrice(
  p_i: number,
  t: number,
  n_outcomes: number,
): number {
  if (t === 0) {
    return 1.0 / n_outcomes;
  }
  return p_i / t;
}

// ─── post_pledge_price ───────────────────────────────────────────────
//
// The displayed outcome price immediately after a pledge of size s
// lands on the outcome (assuming all of s goes into the pool on
// the named outcome). The shape (p_i + s) / (T + s) is the
// standard parimutuel pool-share update.
export function postPledgePrice(
  p_i: number,
  s: number,
  t: number,
): number {
  return (p_i + s) / (t + s);
}

// ─── compute_effective_price ─────────────────────────────────────────
//
// The integrated-slippage effective price per I-042. Three branches
// matching the PL/pgSQL implementation:
//
//   1. Empty pool (T = 0): every outcome's price is 1/n_outcomes.
//      No slippage at the first pledge.
//   2. Small-s Taylor branch (s/T < 1e-6): second-order expansion
//      to avoid catastrophic cancellation in the general formula's
//      (T - p_i)/s × ln(1 + s/T) when s ≪ T. Cutoff matches the
//      engine's 1e-6 threshold in engine/pricing.py.
//   3. General case: 1 − (T − p_i)/s × ln(1 + s/T). Closed-form
//      result of integrating the parimutuel marginal price over the
//      pledge interval. JS Math.log is the natural log.
//
// Throws on non-positive pledge size (matches RPC's RAISE EXCEPTION).
export function computeEffectivePrice(
  p_i: number,
  s: number,
  t: number,
  n_outcomes: number,
): number {
  if (s <= 0) {
    throw new Error(`pledge size must be positive; got ${s}`);
  }
  if (t === 0) {
    return 1.0 / n_outcomes;
  }
  if (s / t < 0.000001) {
    return (p_i / t) + ((t - p_i) * s) / (2 * t * t);
  }
  return 1 - ((t - p_i) / s) * Math.log(1 + s / t);
}

// ─── slippage_pct ────────────────────────────────────────────────────
//
// Fractional slippage paid by a pledge. Three regimes per the
// 2026-05-13 Phase A revision (migration 20260513000001):
//
//   1. pre_price > 0 (normal): (eff_price - pre_price) / pre_price.
//   2. pre_price <= 0 AND n_outcomes undefined / <= 0 (discovery
//      territory, no displayed-prior available): return null.
//      Preserves the legacy 2-arg back-compat behaviour the original
//      Session A migration shipped with, and the 13-case drift
//      test's case 7c relies on this.
//   3. pre_price <= 0 AND n_outcomes > 0 (discovery territory with
//      a known displayed-prior 1/n): return (eff - 1/n) / (1/n).
//      This is the fix for the gate-4 surface (findings §5.5):
//      when an outcome has no prior stake but the pool has stake
//      on other outcomes, the displayed price was 1/n, and the
//      effective price after pledging may be much higher — that
//      ratio is the slippage paid relative to what the user saw.
//
// The third arg is optional so 2-arg call sites (existing 13-case
// drift tests, downstream callers) continue working unchanged.
export function slippagePct(
  pre_price: number,
  eff_price: number,
  n_outcomes?: number,
): number | null {
  if (pre_price > 0) {
    return (eff_price - pre_price) / pre_price;
  }
  if (n_outcomes === undefined || n_outcomes <= 0) {
    return null;
  }
  const displayed_prior = 1.0 / n_outcomes;
  return (eff_price - displayed_prior) / displayed_prior;
}

// ─── payout_units ────────────────────────────────────────────────────
//
// Convenience: how many payout-units a pledge of size `s` claims at
// the given effective price. Matches the RPC's internal computation
// v_units := p_amount / v_eff;
//
// Not a function in the migration (computed inline inside
// place_pledge), but the cockpit uses it for the pre-flight preview
// display ("at this stake you'd claim X payout units if you win").
export function payoutUnits(s: number, effective_price: number): number {
  if (effective_price <= 0) {
    return 0;
  }
  return s / effective_price;
}
